// Region-capture geometry — #332.
//
// `captureSelectedRegion` (ui.ts) used to rasterize the ENTIRE document via
// html-to-image and then crop the small selected rectangle out of a giant
// canvas. Cost scaled with page complexity (DOM size, images, stylesheets),
// not with the selection. On pages many viewports tall this was the dominant
// CPU cost of region capture.
//
// The fix: capture ONLY the viewport (the visible area). The region picker
// locks body scroll while the user drags, so the selection — expressed in
// viewport (clientX/clientY) coordinates — always lives inside the visible
// rectangle. We hand html-to-image a `width`/`height` clamped to the viewport
// plus a `transform: translate(-scrollX, -scrollY)` so the visible slice of
// the document lands at the canvas origin. Cropping then needs no scroll
// offset: the selection's viewport coordinates ARE its coordinates in the
// captured canvas (scaled by the device pixel ratio).
//
// This module holds the pure math so it can be unit-tested without a DOM.
// ui.ts supplies the live window/scroll values + the html-to-image call.
//
// #329 — the viewport-only capture above had a blank-output failure mode that
// only appears on a SCROLLED page whose ROOT element carries a non-visible
// overflow. See `planViewportCapture`'s `overflow` note for the mechanism, and
// `isRasterBlank` / `renderRegionCapture` for the backstop that stops a blank
// capture from ever reaching an upload.

export interface ViewportCaptureInput {
  /** Selection rectangle in CSS pixels, relative to the viewport (clientX/Y). */
  selection: { x: number; y: number; w: number; h: number };
  /** Visible viewport size in CSS pixels (window.innerWidth / innerHeight). */
  viewport: { width: number; height: number };
  /** Current document scroll offset in CSS pixels (window.scrollX / scrollY). */
  scroll: { x: number; y: number };
  /** Device pixel ratio, already clamped by the caller (we clamp again defensively). */
  pixelRatio: number;
}

export interface ViewportCapturePlan {
  /**
   * The complete option object to spread into html-to-image's `toCanvas`.
   * `width`/`height` cap the rasterized surface to the viewport (in CSS px);
   * `style.transform` shifts the visible slice to the origin so a scrolled
   * page still captures what the user sees. `transformOrigin: top left` keeps
   * the translate from being applied around the element's center.
   * `style.overflow` is the #329 fix — see `planViewportCapture`.
   */
  captureOptions: {
    width: number;
    height: number;
    pixelRatio: number;
    style: { transform: string; transformOrigin: string; overflow: string };
  };
  /** Crop canvas pixel dimensions (selection size × pixelRatio). */
  cropSize: { width: number; height: number };
  /**
   * Source rectangle to copy out of the viewport canvas, in device pixels.
   * No scroll offset — the viewport canvas already starts at the visible
   * top-left, so the selection's viewport coords map straight through.
   */
  cropSource: { sx: number; sy: number; sw: number; sh: number };
}

/**
 * Clamp to a sane positive device pixel ratio. The 2x ceiling is applied by the
 * CALLER (`Math.min(window.devicePixelRatio || 1, 2)` in ui.ts) — html-to-image
 * itself imposes no cap. This guard only rejects non-finite / non-positive
 * values, which would otherwise produce a zero-dimension crop canvas.
 */
function safePixelRatio(ratio: number): number {
  if (!Number.isFinite(ratio) || ratio <= 0) return 1;
  return ratio;
}

/**
 * Compute the html-to-image capture options + crop rectangle for a
 * viewport-only region capture.
 *
 * Invariants the math relies on:
 *   - The selection is in viewport coordinates (the picker uses clientX/Y).
 *   - The selection and the scroll offset are read from the same layout, so the
 *     two are consistent with each other. (ui.ts sets `body { overflow: hidden }`
 *     during the picker to discourage scrolling mid-drag. That does NOT lock the
 *     page when the ROOT element owns the scroll — body overflow only propagates
 *     to the viewport while the root's own overflow is `visible` — so treat this
 *     as a nicety, not a guarantee.) The crop source is clamped here against the
 *     canvas size we EXPECT, and then again in `renderRegionCapture` against the
 *     canvas html-to-image actually returned. The second clamp is the load-bearing
 *     one — see `clampSourceToRaster` for the two ways the expectation can be off.
 */
export function planViewportCapture(input: ViewportCaptureInput): ViewportCapturePlan {
  const pixelRatio = safePixelRatio(input.pixelRatio);
  const { selection, viewport, scroll } = input;

  // Capture exactly the visible viewport, shifted so the on-screen slice of a
  // scrolled document lands at the canvas top-left.
  const captureOptions = {
    width: viewport.width,
    height: viewport.height,
    pixelRatio,
    style: {
      transform: `translate(${-scroll.x}px, ${-scroll.y}px)`,
      transformOrigin: 'top left',
      // #329 — THE reason regional captures came back as near-empty PNGs.
      //
      // html-to-image clones the page and copies each node's COMPUTED style
      // onto its clone (clone-node.ts `cloneCSSStyle`, which assigns
      // `getComputedStyle(node).cssText` wholesale). So the cloned <html>
      // inherits whatever overflow the HOST page set on its root element —
      // `overflow-x: hidden` (which makes overflow-y compute to `auto`),
      // `overflow-y: scroll`, or `overflow: auto`. All three are ordinary,
      // widespread page CSS.
      //
      // html-to-image then applies our `width`/`height` (apply-style.ts
      // `applyStyle`), pinning the clone's height to the VIEWPORT height. A
      // viewport-height box with a non-visible overflow is a clipping scroll
      // container: every pixel below the first viewport is discarded from the
      // clone. The `transform` above then slides that already-clipped strip up
      // by the scroll offset — straight off the top of the canvas. On a page
      // scrolled by at least one viewport height, the raster comes back 100%
      // transparent, which encodes to a tiny uniform PNG (measured: 3.6 KB in
      // the harness, 5.8 KB in the wild).
      //
      // `applyStyle` applies `options.style` LAST, after the computed-style
      // copy, so this override wins. It touches only the clone — the live page
      // is never mutated, so no reflow and no layout shift under the user's
      // selection. Nothing extra leaks into the image either: the SVG, the
      // foreignObject, and the raster canvas are all sized to the viewport, so
      // content the host page clipped horizontally still falls outside the
      // frame.
      //
      // Known residual, and why the `isRasterBlank` guard below exists: this
      // reaches the ROOT clone only. A page shaped
      // `html { height: 100% } body { height: 100%; overflow-y: auto }` clips at
      // the BODY instead, and html-to-image exposes no hook to style a non-root
      // clone. That shape still rasterizes blank; the guard makes it fail
      // loudly rather than upload an empty screenshot.
      overflow: 'visible',
    },
  };

  const cropWidth = Math.max(1, Math.round(selection.w * pixelRatio));
  const cropHeight = Math.max(1, Math.round(selection.h * pixelRatio));

  // The captured canvas is `viewport × pixelRatio` device pixels. Clamp the
  // source rectangle so we never sample outside it even if the selection
  // brushed the viewport edge.
  //
  // FLOOR, not round. html-to-image does `canvas.width = canvasWidth * ratio`
  // (index.ts `toCanvas`), and `HTMLCanvasElement.width` is an `unsigned long`
  // IDL attribute, so the assignment TRUNCATES the fraction. A 1365px-wide
  // viewport at a 1.5 device pixel ratio (Windows at 150% scaling) yields a
  // 2047px canvas, not the 2048 that rounding predicts. Overshooting by one
  // device pixel makes the crop read a column that does not exist, which comes
  // back transparent — a 1px see-through stripe down the edge of the screenshot.
  const canvasW = Math.floor(viewport.width * pixelRatio);
  const canvasH = Math.floor(viewport.height * pixelRatio);

  const rawSx = Math.round(selection.x * pixelRatio);
  const rawSy = Math.round(selection.y * pixelRatio);
  const sx = clamp(rawSx, 0, Math.max(0, canvasW - cropWidth));
  const sy = clamp(rawSy, 0, Math.max(0, canvasH - cropHeight));
  const sw = Math.min(cropWidth, canvasW - sx);
  const sh = Math.min(cropHeight, canvasH - sy);

  return {
    captureOptions,
    cropSize: { width: cropWidth, height: cropHeight },
    cropSource: { sx, sy, sw, sh },
  };
}

function clamp(n: number, lo: number, hi: number): number {
  if (n < lo) return lo;
  if (n > hi) return hi;
  return n;
}

/**
 * Re-clamp a planned crop source against the raster html-to-image ACTUALLY
 * returned, rather than the one `planViewportCapture` predicted.
 *
 * The prediction is `floor(viewport × pixelRatio)`, which matches the library's
 * arithmetic exactly — but two things can still move the real canvas underneath
 * it, and neither is observable from the plan:
 *
 *   1. `checkCanvasDimensions` (html-to-image util.ts) silently scales the
 *      canvas down when either axis exceeds the engine's maximum canvas
 *      dimension. An ultrawide display at a 2x pixel ratio can reach it.
 *   2. A future library version changing how `canvas.width` is derived.
 *
 * Reading past a canvas edge is not an exception — `drawImage` just yields
 * transparent pixels there — so an unclamped overshoot degrades silently into
 * a partly see-through screenshot. Clamping against the real thing removes the
 * whole class.
 */
export function clampSourceToRaster(
  source: ViewportCapturePlan['cropSource'],
  rasterWidth: number,
  rasterHeight: number,
): ViewportCapturePlan['cropSource'] {
  const sx = clamp(source.sx, 0, Math.max(0, rasterWidth - source.sw));
  const sy = clamp(source.sy, 0, Math.max(0, rasterHeight - source.sh));
  return {
    sx,
    sy,
    sw: Math.max(0, Math.min(source.sw, rasterWidth - sx)),
    sh: Math.max(0, Math.min(source.sh, rasterHeight - sy)),
  };
}

// ---------------------------------------------------------------------------
// #329 — blank-capture guard + the capture routine that uses it
// ---------------------------------------------------------------------------

/** Height, in canvas pixels, of each strip read by `isRasterBlank`. */
const BLANK_SCAN_STRIP_PX = 64;

/**
 * True when EVERY pixel of `canvas` is fully transparent.
 *
 * The measurement half of the last line of defence against uploading an empty
 * screenshot; `renderRegionCapture` owns the POLICY of what to do with the
 * answer, and only refuses a capture when the whole-viewport raster reads blank.
 * The pre-#329 check in ui.ts only asked whether the raster had non-zero
 * dimensions, which the blank captures always did — 1280x720 of nothing still
 * measures 1280x720. Alpha is the signal that actually distinguishes them.
 *
 * Deliberately alpha-only, and deliberately "every pixel", not "most pixels":
 *   - A capture clamped to the viewport edge leaves a legitimately transparent
 *     margin in the crop, so a partial-transparency threshold would reject good
 *     screenshots.
 *   - Pages that set no background of their own are transparent everywhere the
 *     author painted nothing — commonly the large majority of a capture.
 *   - A uniform-COLOUR test would reject a legitimate capture of a solid-colour
 *     region, which is a perfectly reasonable thing to report a bug about.
 *
 * Reads in horizontal strips so a full-viewport selection on a high-DPI screen
 * never materialises one enormous ImageData, and returns on the first opaque
 * pixel it meets — so the overwhelmingly common (non-blank) case costs one
 * small strip read. Only a genuinely blank canvas is scanned end to end.
 *
 * Any throw (a tainted canvas, a missing 2D context, an engine without
 * getImageData) resolves to `false`. A guess must never block a capture that
 * might be fine; the existing CORS/tainted error path in ui.ts already covers
 * the tainted case when `toBlob` throws for the same reason.
 */
export function isRasterBlank(canvas: HTMLCanvasElement): boolean {
  try {
    const { width, height } = canvas;
    if (!width || !height) return true;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    for (let y = 0; y < height; y += BLANK_SCAN_STRIP_PX) {
      const stripHeight = Math.min(BLANK_SCAN_STRIP_PX, height - y);
      const { data } = ctx.getImageData(0, y, width, stripHeight);
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 0) return false;
      }
    }
    return true;
  } catch {
    return false;
  }
}

export interface RegionCaptureDeps {
  /**
   * html-to-image's `toCanvas`, injected rather than imported so this module
   * stays dependency-free (and so the real-browser regression harness can drive
   * the production code path instead of a copy of it).
   */
  toCanvas: (node: HTMLElement, options: Record<string, unknown>) => Promise<HTMLCanvasElement>;
  /** Element to rasterize — `document.documentElement` in production. */
  root: HTMLElement;
  /** Crop-canvas factory — `() => document.createElement('canvas')` in production. */
  createCanvas: () => HTMLCanvasElement;
}

/** Why a region capture produced nothing usable. */
export type RegionCaptureFailure =
  /** html-to-image returned a zero-dimension canvas. */
  | 'empty_raster'
  /** The crop canvas refused a 2D context. */
  | 'no_2d_context'
  /** The WHOLE viewport rasterized to entirely transparent pixels (#329). */
  | 'blank_raster';

/**
 * Diagnostics attached to a failed capture. Surfaced by the caller so a repeat
 * of #329 is observable from a customer's console instead of inferred from an
 * uploaded file's byte count (AC4).
 */
export interface RegionCaptureDiagnostics {
  rasterWidth: number;
  rasterHeight: number;
  cropWidth: number;
  cropHeight: number;
  source: ViewportCapturePlan['cropSource'];
  transform: string;
}

export type RegionCaptureResult =
  | { ok: true; canvas: HTMLCanvasElement; plan: ViewportCapturePlan }
  | {
      ok: false;
      reason: RegionCaptureFailure;
      plan: ViewportCapturePlan;
      diagnostics: RegionCaptureDiagnostics;
    };

/**
 * Rasterize the viewport, crop the selection out of it, and refuse to hand back
 * a canvas that contains nothing.
 *
 * Lives here rather than inline in ui.ts for one reason: the #329 regression can
 * only be caught by a REAL browser (happy-dom does no layout, no overflow
 * clipping, and no foreignObject rasterisation, so the bug is invisible there),
 * and a browser harness that re-implements the capture would drift away from
 * what actually ships. Keeping the sequence in one exported function means the
 * harness exercises production code.
 */
export async function renderRegionCapture(
  input: ViewportCaptureInput,
  deps: RegionCaptureDeps,
): Promise<RegionCaptureResult> {
  const plan = planViewportCapture(input);

  const raster = await deps.toCanvas(deps.root, {
    cacheBust: false,
    ...plan.captureOptions,
  });

  // Clamp against the canvas that came back, not the one we predicted.
  const source = plan.cropSource;
  const effectiveSource = clampSourceToRaster(source, raster.width, raster.height);

  const diagnostics = (): RegionCaptureDiagnostics => ({
    rasterWidth: raster.width,
    rasterHeight: raster.height,
    cropWidth: plan.cropSize.width,
    cropHeight: plan.cropSize.height,
    source: effectiveSource,
    transform: plan.captureOptions.style.transform,
  });

  if (!raster.width || !raster.height) {
    return { ok: false, reason: 'empty_raster', plan, diagnostics: diagnostics() };
  }

  const crop = deps.createCanvas();
  crop.width = plan.cropSize.width;
  crop.height = plan.cropSize.height;
  const ctx = crop.getContext('2d');
  if (!ctx) {
    return { ok: false, reason: 'no_2d_context', plan, diagnostics: diagnostics() };
  }

  const { sx, sy, sw, sh } = effectiveSource;
  ctx.drawImage(raster, sx, sy, sw, sh, 0, 0, sw, sh);

  // #329 — refuse the capture ONLY when the whole viewport rasterized to
  // nothing. That is the defect's actual signature: the clipped clone produced
  // an entirely transparent surface, so no selection anywhere on the page could
  // have contained pixels.
  //
  // An empty CROP over a painted raster is a different thing entirely, and it is
  // legitimate. Most pages never set a background — the white you see is painted
  // by the browser, not by the page — so on those pages a capture is transparent
  // everywhere the author did not paint something. "This panel is supposed to
  // have content and it's blank" is one of the most ordinary bug reports there
  // is, and rejecting it would make exactly that report unattachable.
  //
  // Order matters for cost, not for correctness: the crop is small and the
  // common case exits on its first strip, so the full-viewport scan is only ever
  // paid for on a capture that is already suspect.
  if (isRasterBlank(crop) && isRasterBlank(raster)) {
    return { ok: false, reason: 'blank_raster', plan, diagnostics: diagnostics() };
  }

  return { ok: true, canvas: crop, plan };
}
